import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

import { client } from '../../client';
import type {
  GetBulkInviteV1OrganizationsOrganizationIdUsersBulkInviteBulkInviteIdGetData,
  GetBulkInviteV1OrganizationsOrganizationIdUsersBulkInviteBulkInviteIdGetResponse,
  GetBulkInviteV1OrganizationsOrganizationIdUsersBulkInviteBulkInviteIdGetResponses,
} from '../../generated/types.gen';

export type GetBulkInviteQueryVariables =
  GetBulkInviteV1OrganizationsOrganizationIdUsersBulkInviteBulkInviteIdGetData['path'];

export type GetBulkInviteQuery = GetBulkInviteV1OrganizationsOrganizationIdUsersBulkInviteBulkInviteIdGetResponse;

const BULK_INVITE_JOB_URL = '/v1/organizations/{organization_id}/users/bulk-invite/{bulk_invite_id}';

const buildKey = ({ organization_id, bulk_invite_id }: GetBulkInviteQueryVariables) =>
  ['GetBulkInvite', { organization_id, bulk_invite_id }] as const;

// Calls the configured client directly rather than a generated SDK function: the bulk-invite
// endpoints are wired from `types.gen.ts` only (see `bulk-invite-csv` / `bulk-invite-validate`),
// which keeps the generated diff additive and out of the way of `main`'s periodic regens.
const fetchBulkInvite = async (variables: GetBulkInviteQueryVariables): Promise<GetBulkInviteQuery> => {
  const { data } = await client.get<
    GetBulkInviteV1OrganizationsOrganizationIdUsersBulkInviteBulkInviteIdGetResponses,
    unknown,
    true
  >({
    url: BULK_INVITE_JOB_URL,
    path: variables,
    throwOnError: true,
  });

  return data;
};

/**
 * Poll one bulk-invite job's status and counters. The dispatch endpoint answers `202 Accepted`
 * before the worker has created any users, so this is what tells the UI the invites actually exist.
 */
export const useGetBulkInviteQuery = <TData = GetBulkInviteQuery, TError = Error>(
  { organization_id, bulk_invite_id }: GetBulkInviteQueryVariables,
  options?: Omit<UseQueryOptions<GetBulkInviteQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetBulkInviteQuery, TError, TData>({
    queryKey: buildKey({ organization_id, bulk_invite_id }),
    queryFn: () => fetchBulkInvite({ organization_id, bulk_invite_id }),
    ...options,
  });
};

useGetBulkInviteQuery.getKey = (variables: GetBulkInviteQueryVariables) => buildKey(variables);

useGetBulkInviteQuery.fetcher = (variables: GetBulkInviteQueryVariables) => () => fetchBulkInvite(variables);
